import { useState, useEffect, useMemo } from 'react';
import { useLazyGetReleaseVersionsForEntityQuery } from '../rtk/api/datagroupsApi';

function entityKey(source) {
  if (!source?.type || !source?.name) return '';
  return `${source.type}-${source.parent || ''}/${source.name}`;
}

export function useEntityVersions({ auth, groupName, latestOnly, schemaSource }) {
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionsError, setVersionsError] = useState(null);

  const [triggerGetVersions] = useLazyGetReleaseVersionsForEntityQuery();

  const key = useMemo(() => entityKey(schemaSource), [schemaSource]);

  useEffect(() => {
    let cancelled = false;

    if (!latestOnly || !key || !groupName || !auth?.user?.id_token) {
      setVersions([]);
      setVersionsError(null);
      return;
    }

    async function fetchVersions() {
      try {
        setVersionsLoading(true);
        setVersionsError(null);

        const { data, error } = await triggerGetVersions(
          {
            groupName,
            type: schemaSource.type,
            parent: schemaSource.parent,
            name: schemaSource.name,
            list: 'version',
          },
          false
        );

        if (error) throw new Error(error.message || 'Failed to fetch versions');
        if (!cancelled) setVersions(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('[EntityVersions] fetch failed', err);
        if (!cancelled) {
          setVersionsError(err.message || 'Failed to fetch versions');
          setVersions([]);
        }
      } finally {
        if (!cancelled) setVersionsLoading(false);
      }
    }

    fetchVersions();
    return () => {
      cancelled = true;
    };
    // `key` captures the entity identity; schemaSource is read inside but only
    // its identity should retrigger a fetch (not a same-entity version change).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, groupName, latestOnly, auth?.user?.id_token, triggerGetVersions]);

  return { versions, versionsLoading, versionsError };
}
